import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface AddCleaningAreaSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd?: (area: any) => void;
  initialData?: any;
  isEditMode?: boolean;
}

interface AreaData {
  name: string;
  squareFootage: number;
  frequency: string;
  productionRate: number;
  hoursPerDay: number;
  daysPerWeek: number;
  specialRequirements: string;
  overheadPercentage: number;
  profitMargin: number;
}

export function AddCleaningAreaSheet({
  isOpen,
  onClose,
  onAdd = () => {},
  initialData,
  isEditMode = false,
}: AddCleaningAreaSheetProps) {
  const defaultAreaData: AreaData = {
    name: "",
    squareFootage: 0,
    frequency: "5_per_week",
    productionRate: 1000, // More realistic production rate for cleaning
    hoursPerDay: 0,
    daysPerWeek: 5,
    specialRequirements: "",
    overheadPercentage: 15, // Default value
    profitMargin: 10, // Default value
  };

  const [areaData, setAreaData] = useState<AreaData>(defaultAreaData);

  // Helper function to extract days per week from frequency value
  const getDaysPerWeekFromFrequency = (frequency: string): number => {
    const match = frequency.match(/^(\d+)_per_week$/);
    if (match) {
      return parseInt(match[1], 10);
    }
    // Handle special cases
    switch (frequency) {
      case "2_per_month":
        return 0.5; // Approximately 2 days per month = 0.5 days per week
      case "1_per_month":
        return 0.25; // Approximately 1 day per month = 0.25 days per week
      default:
        return 5; // Default fallback
    }
  };

  // Initialize with initialData if in edit mode
  useEffect(() => {
    if (isEditMode && initialData) {
      const frequency = initialData.frequency || "5_per_week";
      const daysPerWeek = initialData.daysPerWeek || getDaysPerWeekFromFrequency(frequency);

      setAreaData({
        name: initialData.name || "",
        squareFootage: initialData.squareFootage || 0,
        frequency: frequency,
        productionRate: initialData.productionRate || 1000,
        hoursPerDay: initialData.hoursPerDay || 0,
        daysPerWeek: daysPerWeek,
        specialRequirements: initialData.specialRequirements || "",
        overheadPercentage: initialData.overheadPercentage || 15,
        profitMargin: initialData.profitMargin || 10,
      });
    }
  }, [isEditMode, initialData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;

    // Improved number parsing with better type safety
    let newValue: string | number;
    if (id === "name" || id === "specialRequirements") {
      newValue = value;
    } else {
      // Handle empty string case properly
      if (value === "" || value === null || value === undefined) {
        newValue = 0;
      } else {
        const parsed = parseFloat(value);
        newValue = isNaN(parsed) ? 0 : parsed;
      }
    }

    // Create updated data with the new value
    const updatedData = {
      ...areaData,
      [id]: newValue,
    };

    // Calculate hours per day if both square footage and production rate are provided
    if (
      (id === "squareFootage" || id === "productionRate") &&
      (id === "squareFootage" ? (newValue as number) : areaData.squareFootage) > 0 &&
      (id === "productionRate" ? (newValue as number) : areaData.productionRate) > 0
    ) {
      const squareFootage = id === "squareFootage" ? (newValue as number) : areaData.squareFootage;
      const productionRate =
        id === "productionRate" ? (newValue as number) : areaData.productionRate;

      const calculatedHours = squareFootage / productionRate;
      updatedData.hoursPerDay = parseFloat(calculatedHours.toFixed(2));
    }

    setAreaData(updatedData);
  };

  const handleSelectChange = (value: string) => {
    const daysPerWeek = getDaysPerWeekFromFrequency(value);

    setAreaData({
      ...areaData,
      frequency: value,
      daysPerWeek: daysPerWeek,
    });
  };

  const handleSubmit = () => {
    if (!areaData.name || !areaData.squareFootage) {
      return; // Don't submit if required fields are missing
    }

    // Recalculate hours per day to ensure it's correct
    const hoursPerDay =
      areaData.squareFootage > 0 && areaData.productionRate > 0
        ? parseFloat((areaData.squareFootage / areaData.productionRate).toFixed(2))
        : areaData.hoursPerDay;

    // Calculate weekly hours
    const calculatedHours = parseFloat((hoursPerDay * areaData.daysPerWeek).toFixed(2));

    const laborRate = 35; // Default labor rate
    const cost = calculatedHours * laborRate;
    const totalPrice = cost * (1 + (areaData.overheadPercentage + areaData.profitMargin) / 100);

    const areaToSubmit = {
      id: isEditMode && initialData ? initialData.id : Date.now().toString(),
      name: areaData.name,
      type: "cleaning",
      squareFootage: areaData.squareFootage,
      frequency: areaData.frequency,
      productionRate: areaData.productionRate,
      laborRate: laborRate,
      hoursPerDay: hoursPerDay,
      daysPerWeek: areaData.daysPerWeek,
      specialRequirements: areaData.specialRequirements,
      calculatedHours: calculatedHours,
      hours: calculatedHours,
      cost: cost,
      serviceDays: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      description: areaData.specialRequirements || "",
      pricingMethod: "hourly", // or "squareFootage" based on your needs
      hourlyRate: laborRate,
      overheadPercentage: areaData.overheadPercentage,
      profitMargin: areaData.profitMargin,
      totalPrice: totalPrice,
    };

    onAdd(areaToSubmit);
    onClose();
  };
  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[500px] sm:max-w-[500px]">
        <SheetHeader>
          <SheetTitle>{isEditMode ? "Edit" : "Add"} Cleaning Area</SheetTitle>
          <SheetDescription>
            {isEditMode
              ? "Update the details for this cleaning area."
              : "Enter the details for the new cleaning area."}
          </SheetDescription>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-180px)] pr-4">
          <div className="space-y-6 px-2 py-6">
            <div className="space-y-2">
              <Label htmlFor="area">Area Name</Label>
              <Input
                id="name"
                placeholder="e.g. Main Office, Lobby"
                value={areaData.name}
                onChange={handleInputChange}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="square-footage">Square Footage</Label>
              <Input
                id="squareFootage"
                type="number"
                placeholder="Enter square footage"
                value={areaData.squareFootage || ""}
                onChange={handleInputChange}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="frequency">Cleaning Frequency</Label>
              <Select value={areaData.frequency} onValueChange={handleSelectChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select frequency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7_per_week">7 Days per Week</SelectItem>
                  <SelectItem value="6_per_week">6 Days per Week</SelectItem>
                  <SelectItem value="5_per_week">5 Days per Week</SelectItem>
                  <SelectItem value="4_per_week">4 Days per Week</SelectItem>
                  <SelectItem value="3_per_week">3 Days per Week</SelectItem>
                  <SelectItem value="2_per_week">2 Days per Week</SelectItem>
                  <SelectItem value="1_per_week">1 Day per Week</SelectItem>
                  <SelectItem value="2_per_month">Bi-Monthly</SelectItem>
                  <SelectItem value="1_per_month">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="production-rate">Production Rate (sq ft/hr)</Label>
              <Input
                id="productionRate"
                type="number"
                placeholder="Enter production rate"
                value={areaData.productionRate || ""}
                onChange={handleInputChange}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="hours-per-day">Hours Per Day</Label>
              <Input
                id="hoursPerDay"
                type="number"
                placeholder="Hours per day"
                value={areaData.hoursPerDay || ""}
                onChange={handleInputChange}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="days-per-week">Days Per Week</Label>
              <Input
                id="daysPerWeek"
                type="number"
                placeholder="Days per week"
                value={areaData.daysPerWeek}
                onChange={handleInputChange}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="special-requirements">Special Requirements</Label>
              <Input
                id="specialRequirements"
                placeholder="Any special cleaning requirements"
                value={areaData.specialRequirements}
                onChange={handleInputChange}
              />
            </div>

            <Button
              className="w-full"
              onClick={handleSubmit}
              disabled={!areaData.name || !areaData.squareFootage}
            >
              {isEditMode ? "Update Cleaning Area" : "Add Cleaning Area"}
            </Button>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
